// Playing area bookings, the MEMBER and PUBLIC side (BOOKINGS_PLAN.md B5,
// B10, B11, B13, B14, B18, BE17, BE22, BE27). Pure shapes and words only, so
// they are unit-tested without Vuetify: the areas × time grid (smallest slot
// rows, longer slots span), how long a booking may run from a cell, the day
// chips, the club page's "Right now" rows and the my-bookings words. The
// network half is composables/useBookingsMember.js. The API's own lines
// (line, cancelLine, priceLine, reason) are shown as they arrive.
import {formatMinor} from '~/utils/adminActionCopy';
import {clockWord} from '~/utils/bookings';
import {overrideLines} from '~/utils/bookingAdminOverrides';

/** One icon for bookings everywhere (never reused for anything else). */
export const BOOKING_ICON = 'mdi-timetable';

/** X58 (ruling 9, R23a L4): every booking amount keeps its pence ("paid £4.00"), so money and moneyExact are one rule. */
export const money = (minor, currency) => formatMinor(minor ?? 0, currency, {exact: true});
/** X54 (ruling 9): always two decimal places ("£6.00", "£12.50") for the grid, the sheet and the drag ghost; "Free" stays the word for zero. */
export const moneyExact = (minor, currency) => formatMinor(minor ?? 0, currency, {exact: true});

/**
 * What the viewer pays or paid on a booking: their own share of a split match booking, else the whole price (R4 NM1).
 * X36: on a confirmed booking the club lengthened, Pay now asks for the extra time alone.
 */
export const payAmount = (b) => (owesExtra(b) ? b.extraDueMinor : b?.yourShareMinor != null ? b.yourShareMinor : (b?.totalMinor ?? 0));

/** X36: the club lengthened the booking and sent a pay link for the difference; the viewer is the payer. */
export const owesExtra = (b) => b?.status === 'Confirmed' && (b?.extraDueMinor ?? 0) > 0 && !!b?.canPayExtra;

/**
 * R6 NM1: the viewer still owes money on this booking: it is waiting on a card, they have a share (a match player who
 * pays nothing has none) and that share isn't already paid (the side that paid its half of a split booking is not asked again).
 */
export const viewerOwes = (b) => (b?.status === 'PendingPayment' && (b?.yourShareMinor ?? 0) > 0 && !b?.yourSharePaid) || owesExtra(b);

/**
 * "paid £3" on a row: the whole booking is paid, or the viewer's own half of a split one still waiting on the other side.
 * X74 (R25 L8): "paid £12.00 · refunded £6.00" once the club has sent part of it back (a shorten).
 */
export function paidWords(b) {
  if (owesExtra(b)) return `${b.extraDueLine || money(b.extraDueMinor, b.currency) + ' to pay for the extra time'}`;
  const amount = payAmount(b);
  if (amount <= 0) return null;
  if (b?.paid || (b?.yourSharePaid && b?.status === 'PendingPayment')) {
    const paid = `paid ${money(amount, b.currency)}`;
    return (b?.refundedMinor ?? 0) > 0 ? `${paid} · refunded ${money(b.refundedMinor, b.currency)}` : paid;
  }
  return null;
}

/**
 * X94 (Grant 2026-10-21): the admin booking dialog's Price line, read as the All bookings list reads it: "Free", "paid
 * £6.00", "paid £6.00 · refunded £6.00" (paid, then refunded: it said "£6.00 · not paid" because a booking refunded in
 * full is no longer `paid`), "£6.00 · waiting for payment", "£6.00 · not paid". `money(minor)` formats in its currency.
 */
export function adminPaidLine(b, fmt = (m) => money(m, b?.currency)) {
  if (!b) return '';
  if (!b.totalMinor) return 'Free';
  const words = paidWords(b);
  if (words) return words;
  const refunded = b.refundedMinor ?? 0;
  // A card charge that has been refunded (in full, or a cancelled booking): it was paid, then refunded.
  if (refunded > 0 && (b.paymentChargeId || b.paid || b.status === 'Cancelled' || b.status === 'Released' || b.status === 'NoShow')) {
    return `paid ${fmt(Math.max(b.totalMinor, refunded))} · refunded ${fmt(refunded)}`;
  }
  return `${fmt(b.totalMinor)} · ${b.status === 'PendingPayment' ? 'waiting for payment' : 'not paid'}`;
}

/**
 * X74 (R25 L1): what the visitor sheet charges for THIS cell: the group's own visitor price (the public page carries one
 * allowance per group) times the slots picked, with the per-slot price beside it for a longer booking; "Free" on a free
 * group; the club's cheapest line only when the page has no groups (an older API).
 *   {line: "£8.50" | "£17.00 (£8.50 a slot)" | "Free" | "From £6.50 a slot…", amountMinor: 850 | 0 | null}
 */
export function visitorPriceWords(page, groupId, slots = 1) {
  const group = (page?.groups ?? []).find((g) => g.id === groupId) ?? null;
  const visitor = group?.visitor ?? null;
  if (!visitor) return {line: page?.visitorPriceLine || 'Free', amountMinor: null};
  const rate = visitor.priceMinor ?? 0;
  const n = Math.max(1, Number(slots) || 1);
  if (rate <= 0) return {line: 'Free', amountMinor: 0};
  const total = rate * n;
  return {line: n > 1 ? `${money(total, page?.currency)} (${money(rate, page?.currency)} a slot)` : money(total, page?.currency), amountMinor: total};
}

/** "the slot is held until Sun 18 Oct, 6:43am": the day as well as the time, since a hold can run into tomorrow (R4 Low 11). */
export function heldUntilLine(b) {
  if (owesExtra(b)) return 'the club made it longer; nothing is charged until you pay';
  if (!b?.paymentDueAt) return 'the slot is held for you';
  const d = new Date(b.paymentDueAt);
  const day = d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'});
  const time = d.toLocaleTimeString('en-GB', {hour: 'numeric', minute: '2-digit', hour12: true}).replace(/\s?(am|pm)$/i, '$1').toLowerCase();
  return `the slot is held until ${day}, ${time}`;
}

// ── Cell statuses (AvailabilityAreaCellResponse.Status, BE27) ──
export const CELL = Object.freeze({
  free: {word: 'Available', cls: 'free', legend: true},
  booked: {word: 'Booked', cls: 'booked', legend: true},
  mine: {word: 'Yours', cls: 'mine', legend: true},
  match: {word: 'Match', cls: 'match', legend: true},
  matchesOnly: {word: 'Matches only', cls: 'matches-only', legend: true},
  open: {word: 'Open play', cls: 'open', legend: true},
  frozen: {word: 'Frozen', cls: 'frozen', legend: true},
  closed: {word: 'Closed', cls: 'closed', legend: true},
  notYetOpen: {word: 'Not yet open', cls: 'not-yet-open', legend: false},
});

/** The legend under the grid, in the order the mockup shows them. */
export const LEGEND = Object.freeze(['free', 'open', 'booked', 'mine', 'match', 'matchesOnly', 'frozen', 'closed']
    .map((key) => ({key, word: CELL[key].word, cls: CELL[key].cls})));

export function cellMeta(status) {
  return CELL[status] ?? CELL.closed;
}

/**
 * What a cell shows: the API's label when it has one ("You", initials,
 * "Booked", "Match", "Meet", "Club", the name for admins), else the status
 * word. A free cell shows nothing (the + is drawn by the grid).
 */
export function cellWord(cell) {
  if (!cell) return '';
  if (cell.status === 'free') return '';
  if (cell.label) return cell.label;
  return cellMeta(cell.status).word;
}

/** A cell the viewer may tap to book: free, in a bookable slot. */
export function cellBookable(slot, cell) {
  return !!slot?.bookable && cell?.status === 'free';
}

// ── Times ──
/** "18:00" → "6pm", "18:30" → "6:30pm", "00:00" → "12am", "01:00 (+00:00)" → "1am (+00:00)" (BE15). */
export function slotTimeWord(label) {
  const text = String(label ?? '').trim();
  const m = text.match(/^(\d{1,2}):(\d{2})(.*)$/);
  if (!m) return text;
  const h24 = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const rest = m[3] ? ' ' + m[3].trim() : '';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 < 12 ? 'am' : 'pm';
  return (min === 0 ? `${h12}${suffix}` : `${h12}:${String(min).padStart(2, '0')}${suffix}`) + rest;
}

/** "40 min" / "1 h" / "1 h 20" / "2 h" / "All day". X78 (R27 N10): one duration style everywhere, the calendar's (bookingDrag.durationWords, the API's ShortDuration). */
export function durationWord(minutes, allDay = false) {
  if (allDay || minutes == null) return 'All day';
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

/** "Cancel up to 1 hour before" / "Cancel up to 2 days before" / "Cancel any time". */
export function cutoffLine(minutes) {
  if (minutes == null) return '';
  if (minutes === 0) return 'Cancel any time';
  if (minutes < 60) return `Cancel up to ${minutes} min before`;
  if (minutes < 1440 && minutes % 60 === 0) return `Cancel up to ${minutes / 60} hour${minutes === 60 ? '' : 's'} before`;
  if (minutes >= 1440 && minutes % 1440 === 0) return `Cancel up to ${minutes / 1440} day${minutes === 1440 ? '' : 's'} before`;
  return `Cancel up to ${Math.round(minutes / 60)} hours before`;
}

// ── Dates (the club's zone) ──
/** yyyy-MM-dd of `now` in an IANA zone (the API's day keys are in the club's zone). */
export function dateInZone(zone, now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone: zone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit'})
        .formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Day chip words: {top: "Today" | "Fri", num: "17", date}. */
export function dayChip(date, todayDate) {
  const d = new Date(`${date}T12:00:00Z`);
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return {date, top: date === todayDate ? 'Today' : weekday, num: String(d.getUTCDate()), weekday};
}

/** "Thursday 16 October" from a day key. */
export function longDate(date) {
  const d = new Date(`${date}T12:00:00Z`);
  return d.toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'});
}

/** Whole days from one day key to another (negative when `to` is earlier). */
export function daysBetween(from, to) {
  const a = new Date(`${from}T12:00:00Z`);
  const b = new Date(`${to}T12:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((b - a) / 86400000);
}

export const MAX_GRID_DAYS = 28;

/**
 * How many days the grid asks for (BE17): the viewer's Book up to, today, and
 * one more so the first day beyond the window shows dashed with its opening
 * date. A ?date= further out stretches the ask to reach it (never past 28).
 */
export function daysToLoad(daysAhead, {wantedDate = null, todayDate = null} = {}) {
  let n = (daysAhead ?? 7) + 2;
  if (wantedDate && todayDate) {
    const diff = daysBetween(todayDate, wantedDate);
    if (diff != null && diff >= 0) n = Math.max(n, diff + 1);
  }
  return Math.min(MAX_GRID_DAYS, Math.max(1, n));
}

/**
 * Why a ?date= isn't the day shown (so the grid never passes today off as it
 * silently): null when the day is in the list or nothing was asked for.
 */
export function dateNotice(wantedDate, dayList, todayDate) {
  if (!wantedDate || (dayList ?? []).some((d) => d.date === wantedDate)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(wantedDate) || daysBetween(todayDate, wantedDate) == null) return "That date couldn't be read, so today is shown.";
  const diff = daysBetween(todayDate, wantedDate);
  if (diff < 0) return `${longDate(wantedDate)} has passed, so today is shown.`;
  return `${longDate(wantedDate)} is too far ahead to show yet, so today is shown.`;
}

/** "today" / "tomorrow" / "Fri 17 Oct" for the sheet title. */
export function dayWord(date, todayDate) {
  if (date === todayDate) return 'today';
  const d = new Date(`${date}T12:00:00Z`);
  const t = new Date(`${todayDate}T12:00:00Z`);
  if (Math.round((d - t) / 86400000) === 1) return 'tomorrow';
  return d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'});
}

// ── The grid (B5, B18, BE27) ──
/** The areas that take part: for the chosen sport (an area with no sports plays any), in the club's order. */
export function areasForSport(areas, sportId = null) {
  return [...(areas ?? [])]
      .filter((a) => !sportId || !(a.sportIds?.length) || a.sportIds.includes(sportId))
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name)));
}

/**
 * One day of a club as table rows. Groups may have different slot lengths, so
 * the rows are the smallest step (every distinct slot start across the groups)
 * and an area's slot spans as many rows as it covers.
 *
 *   {rows: [{startsAt, label}], columns: [{area, cells: [{row, span, slot, cell}]}],
 *    body: [{row, cells: [{kind: 'cell'|'none', area, span?, slot?, cell?}]}], empty}
 *
 * `body` is ready for a <table>: a spanning cell appears once, on its first
 * row (rowspan carries it); rows it covers hold nothing for that column.
 */
export function buildGrid(day, {groups = [], areas = [], sportId = null} = {}) {
  const visible = areasForSport(areas, sportId);
  const groupIds = new Set(visible.map((a) => a.groupId));
  const slotsByGroup = new Map();
  for (const g of day?.groups ?? []) {
    if (!groupIds.has(g.groupId)) continue;
    slotsByGroup.set(g.groupId, [...(g.slots ?? [])].sort((a, b) => a.startsAt.localeCompare(b.startsAt)));
  }
  const starts = new Map();
  for (const slots of slotsByGroup.values()) {
    for (const s of slots) if (!starts.has(s.startsAt)) starts.set(s.startsAt, s.label);
  }
  const rows = [...starts.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([startsAt, label]) => ({startsAt, label}));
  const rowIndex = new Map(rows.map((r, i) => [r.startsAt, i]));
  const spanOf = (slot) => rows.filter((r) => r.startsAt >= slot.startsAt && r.startsAt < slot.endsAt).length || 1;

  const columns = visible.map((area) => {
    const slots = slotsByGroup.get(area.groupId) ?? [];
    const cells = slots.map((slot) => ({
      row: rowIndex.get(slot.startsAt),
      span: spanOf(slot),
      slot,
      cell: (slot.areas ?? []).find((c) => c.areaId === area.id) ?? null,
      group: (groups ?? []).find((g) => g.id === area.groupId) ?? null,
    })).filter((c) => c.cell);
    return {area, cells};
  });

  const body = rows.map((row, i) => {
    const cells = [];
    for (const col of columns) {
      const here = col.cells.find((c) => c.row === i);
      if (here) { cells.push({kind: 'cell', area: col.area, ...here}); continue; }
      const covered = col.cells.some((c) => c.row < i && c.row + c.span > i);
      if (!covered) cells.push({kind: 'none', area: col.area});
    }
    return {row, cells};
  });

  return {rows, columns, body, empty: rows.length === 0 || columns.length === 0};
}

/**
 * How many slots a booking may run from a cell: the free, bookable slots that
 * follow without a gap in that area, capped by the viewer's longest booking
 * (BE18: never past the band, never into Matches only or Closed time; the API
 * only marks those slots free when they can be booked).
 */
export function maxSlotsFrom(column, index, {slotMinutes = 60, maxDurationMinutes = null, allDay = false} = {}) {
  const cells = column?.cells ?? [];
  if (index < 0 || index >= cells.length) return 0;
  let count = 0;
  for (let i = index; i < cells.length; i++) {
    const c = cells[i];
    if (!cellBookable(c.slot, c.cell)) break;
    if (i > index && cells[i - 1].slot.endsAt !== c.slot.startsAt) break;
    count++;
  }
  if (!allDay && maxDurationMinutes != null && slotMinutes > 0) {
    count = Math.min(count, Math.max(1, Math.floor(maxDurationMinutes / slotMinutes)));
  }
  return count;
}

/** The "How long" pills: [{slots: 1, label: '1 hour'}, …]. */
/**
 * How long the booking sheet opens at: the length the player chose (one slot
 * for a click, the dragged range when the grid passes one), clamped to
 * 1..maxSlots. Never the longest allowed: a click used to open at the viewer's
 * limit (2 h) and had to be shortened every time (Grant 2026-10-19).
 */
export function openingSlots(initialSlots, maxSlots) {
  const want = Number.isFinite(Number(initialSlots)) ? Math.round(Number(initialSlots)) : 1;
  const cap = Math.max(1, Math.floor(Number(maxSlots) || 0));
  return Math.min(cap, Math.max(1, want));
}

export function durationOptions(maxSlots, slotMinutes) {
  const out = [];
  for (let n = 1; n <= Math.max(1, maxSlots); n++) out.push({slots: n, label: durationWord(n * slotMinutes)});
  return out;
}

/**
 * Grant 2026-11-02 ("On the booking bottom sheet, the How long section looks messy as chips. Why not have a number of
 * slots stepper?"): the member sheet's How long is a − / + stepper, one slot a press. Its top is maxSlotsFrom (a
 * member: the free run capped by their longest booking; an admin: the free run alone, "Admin can do what they want
 * with a warning") and this says WHY it stops there, for the line under the stepper when + is off:
 *   {maxSlots, stop: 'allowance' | 'booked' | 'notBookable' | 'dayEnd', at: '21:00' | null}
 * 'allowance' when the longest booking cuts the free run; else what the slot after the run holds: a booking (booked,
 * yours, a match), closed / frozen / matches-only / not-yet-open time or a gap in the hours (notBookable), or no slot
 * at all (dayEnd). `at` is the run's end on the club's 24-hour clock (runRange's words).
 */
export function lengthStop(column, index, {slotMinutes = 60, maxDurationMinutes = null, allDay = false} = {}) {
  const cells = column?.cells ?? [];
  const run = maxSlotsFrom(column, index, {slotMinutes, allDay: true});
  const maxSlots = maxSlotsFrom(column, index, {slotMinutes, maxDurationMinutes, allDay});
  if (!run) return {maxSlots: Math.max(1, maxSlots), stop: 'dayEnd', at: null};
  if (maxSlots < run) return {maxSlots, stop: 'allowance', at: null};
  const last = cells[index + run - 1];
  const next = cells[index + run];
  const at = endLabelOf(last?.slot, {h24: true}) || null;
  if (!next) return {maxSlots, stop: 'dayEnd', at};
  if (last?.slot?.endsAt !== next.slot?.startsAt) return {maxSlots, stop: 'notBookable', at};
  const status = next.cell?.status;
  return {maxSlots, stop: status === 'booked' || status === 'mine' || status === 'match' ? 'booked' : 'notBookable', at};
}

/**
 * The line under the stepper when + is off: "Longest you can book is 2 h" / "The court is booked from 21:00" /
 * "The court can't be booked from 21:00" / "Bookings end at 22:00"; '' when there is nothing to say. `noun` is the
 * club's own word for the area (areaWords).
 */
export function longerReason(limit, {noun = 'playing area', maxDurationMinutes = null} = {}) {
  if (!limit) return '';
  const at = limit.at;
  switch (limit.stop) {
    case 'allowance': return maxDurationMinutes != null ? `Longest you can book is ${durationWord(maxDurationMinutes)}` : '';
    case 'booked': return at ? `The ${noun} is booked from ${at}` : '';
    case 'notBookable': return at ? `The ${noun} can't be booked from ${at}` : '';
    case 'dayEnd': return at ? `Bookings end at ${at}` : '';
    default: return '';
  }
}

/**
 * The visitor sheet's longest run from a cell: the Visitors row's longest
 * booking when the public page says it (visitorAllDay / visitorMaxDurationMinutes),
 * else the free run up to 4 slots (an older API) and the API's own refusal.
 */
export function visitorMaxSlots(page, column, index, slotMinutes = 60) {
  if (page?.visitorAllDay) return maxSlotsFrom(column, index, {slotMinutes, allDay: true});
  if (page?.visitorMaxDurationMinutes != null) return maxSlotsFrom(column, index, {slotMinutes, maxDurationMinutes: page.visitorMaxDurationMinutes});
  return Math.min(4, maxSlotsFrom(column, index, {slotMinutes, allDay: true}));
}

/** "Visitors can book up to 1 hour at a time." / "" when the page doesn't say. */
export function visitorLongestLine(page) {
  if (page?.visitorAllDay) return 'Visitors can book all day.';
  if (page?.visitorMaxDurationMinutes != null) return `Visitors can book up to ${durationWord(page.visitorMaxDurationMinutes)} at a time.`;
  return '';
}

/** "Full members can book up to 3 hours." / "You can book all day." */
export function longestLine(viewer, {groupAllowance = null} = {}) {
  const a = groupAllowance ?? viewer;
  if (!a) return '';
  if (a.allDay || a.maxDurationMinutes == null) return 'You can book all day.';
  return `You can book up to ${durationWord(a.maxDurationMinutes)} at a time.`;
}

/** The cells' end label for a run of n slots from a cell ("8pm–10pm"). */
export function runRange(column, index, n, {h24 = false} = {}) {
  const cells = column?.cells ?? [];
  const first = cells[index];
  const last = cells[Math.min(cells.length - 1, index + Math.max(1, n) - 1)];
  if (!first) return '';
  const endLabel = endLabelOf(last?.slot ?? first.slot, {h24});
  return `${h24 ? clockWord(first.slot.label) : slotTimeWord(first.slot.label)}–${endLabel}`;
}

/** A slot's end as a local word, from the slot's own label and length. */
export function endLabelOf(slot, {h24 = false} = {}) {
  if (!slot) return '';
  const m = String(slot.label ?? '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  const minutes = Math.round((new Date(slot.endsAt) - new Date(slot.startsAt)) / 60000);
  const total = (parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + minutes) % 1440;
  const hhmm = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  // X94 (R35 L2): the member sheet beside the grid says the grid's 24-hour clock ("20:00–22:00", a midnight end "24:00").
  return h24 ? clockWord(total === 0 ? '24:00' : hhmm, {end: true}) : slotTimeWord(hhmm);
}

// ── "Right now" (club page, B16's words) ──
/**
 * X226: whether the club takes bookings, for the club page's availability card and "Your bookings": the switch AND
 * something bookable (bookingsActive: an area that takes bookings, or Matches only hours). Bookings are on for every
 * club by default since X226, so the switch alone would draw an empty card on clubs that never chose an area. An
 * older API without bookingsActive keeps the switch.
 */
export function takesBookings(availability) {
  return !!availability?.bookingsEnabled && availability?.bookingsActive !== false;
}

/**
 * What each area is doing at `now`: the slot of its group covering now on the
 * first day, else open play (outside every band there is nothing to book).
 *   [{area, status, word, label}]
 */
export function rightNowRows(availability, now = new Date()) {
  if (!availability?.bookingsEnabled) return [];
  const day = availability.dayList?.[0];
  const iso = now.toISOString();
  return areasForSport(availability.areas).map((area) => {
    const slots = day?.groups?.find((g) => g.groupId === area.groupId)?.slots ?? [];
    const slot = slots.find((s) => s.startsAt <= iso && s.endsAt > iso);
    const cell = slot?.areas?.find((c) => c.areaId === area.id) ?? null;
    // R21 N9: a club booking is the club's, not the admin who made it: never "You" / yours here.
    if (isClubBooking(cell)) return {area, status: 'booked', word: 'The club', mine: false};
    const status = cell?.status ?? 'open';
    const word = !cell ? 'Open play' : (status === 'free' ? 'Available' : (cellWord(cell) || cellMeta(status).word));
    return {area, status, word, mine: !!cell?.mine};
  });
}

/** A club booking ("The club", kind Admin): the club's, whoever made it (X44's ruling, R21 N9). */
export function isClubBooking(b) {
  return b?.kind === 'Admin';
}

/** The viewer's own bookings: a club booking an admin made is left out of "Your bookings" and its count (R21 N9). */
export function personalBookings(list) {
  return (list ?? []).filter((b) => !isClubBooking(b));
}

// ── My bookings (B14, BE22) ──
export function bookingStatusChip(b) {
  // R6 NM1: the viewer's half of a split booking is in; it's the other side that is still to pay.
  if (b?.status === 'PendingPayment' && b?.yourSharePaid) return {label: 'Waiting for the other side', cls: 'tl-chip--warn'};
  switch (b?.status) {
    case 'Confirmed': return {label: 'Booked', cls: 'tl-chip--ok'};
    case 'PendingPayment': return {label: 'Awaiting payment', cls: 'tl-chip--warn'};
    case 'Held': return {label: 'Held', cls: 'tl-chip--warn'};
    case 'Queued': return {label: 'Booking opens later', cls: ''};
    case 'Cancelled': return {label: 'Cancelled', cls: ''};
    case 'Released': return {label: 'Released', cls: ''};
    case 'NoShow': return {label: 'No-show', cls: ''};
    default: return {label: String(b?.status ?? ''), cls: ''};
  }
}

/** Match / Meet / Club tags on a row; play bookings carry none. */
export function bookingKindChip(b) {
  switch (b?.kind) {
    case 'Match': return 'Match';
    case 'MeetHold': return 'Meet';
    case 'Admin': return 'Club';
    case 'Visitor': return 'Visitor';
    default: return null;
  }
}

export const LIVE_STATUSES = Object.freeze(['Queued', 'Held', 'PendingPayment', 'Confirmed']);

/** Upcoming (live and not yet over) first, then the rest, newest first. */
export function splitBookings(list, now = new Date()) {
  const iso = now.toISOString();
  const upcoming = [];
  const past = [];
  for (const b of list ?? []) {
    if (LIVE_STATUSES.includes(b.status) && b.endsAt > iso) upcoming.push(b);
    else past.push(b);
  }
  upcoming.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  past.sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  return {upcoming, past};
}

/**
 * X76 (R26 L1): the visitor page's money words: "refunded £8.50" once any came back, "paid £8.50" when the charge
 * settled (the API's `paid`; an older API without it is taken as paid), nothing for a priced hold that was never paid
 * (closed with "Later" and then cancelled) or a free booking.
 */
export function visitorMoneyWords(page) {
  if (!page || !(page.priceMinor > 0)) return '';
  if (page.refundedMinor > 0) return `refunded ${money(page.refundedMinor, page.currency)}`;
  const paid = page.paid ?? true;
  return paid ? `paid ${money(page.priceMinor, page.currency)}` : '';
}

/**
 * X76 (R26 L5): the club card's day line per group ([{name, note}]). The API's one line for the day is the union of
 * every group's hours ("Booking needed 8am–10pm · Closed 9pm–10pm" when one group closed early and the others did not),
 * so with more than one group each group's own line is shown under its name; one group, or every group saying the same,
 * keeps the plain line.
 */
export function dayNotes(availability) {
  const day = availability?.dayList?.[0];
  if (!day) return [];
  const names = new Map((availability.groups ?? []).map((g) => [g.id, g.name]));
  const perGroup = (day.groups ?? []).filter((g) => g.note).map((g) => ({name: names.get(g.groupId) ?? null, note: g.note}));
  if (perGroup.length > 1 && new Set(perGroup.map((g) => g.note)).size > 1) return perGroup;
  return day.note ? [{name: null, note: day.note}] : [];
}

/** "With Sam Morgan, Ali (guest)". */
export function withLine(b) {
  const names = (b?.participants ?? []).map((p) => p.name ? (p.isGuest ? `${p.name} (guest)` : p.name) : null).filter(Boolean);
  return names.length ? `With ${names.join(', ')}` : '';
}

/**
 * The cancel confirmation (B11/B14/BE22): a refund before the cut-off, none
 * after; a free booking just goes back.
 */
export function cancelWords(b, nowIso = new Date().toISOString()) {
  const paid = !!b?.paid && (b.totalMinor ?? 0) > 0;
  const refund = b?.refundIfCancelledMinor ?? 0;
  const pastCutoff = !!b?.cancelCutoffAt && b.cancelCutoffAt <= nowIso;
  let message;
  if (paid && refund > 0) message = `You'll be refunded ${money(refund, b.currency)}.`;
  else if (paid) message = pastCutoff ? "It's past the cut-off, so there's no refund now." : "There's no refund for this booking.";
  else message = 'The slot goes back to the club for someone else.';
  return {
    title: 'Cancel this booking?',
    message: `${b?.line ? b.line + '. ' : ''}${message}`,
    confirmText: paid && refund === 0 ? 'Cancel, no refund' : 'Cancel booking',
  };
}

/** The one-tap Release (B14): the reminder's "I won't make it". */
export function releaseWords(b) {
  const paid = !!b?.paid && (b.totalMinor ?? 0) > 0;
  const refund = b?.refundIfCancelledMinor ?? 0;
  return {
    title: 'Release this booking?',
    message: paid && refund === 0 ? 'Someone else can have the slot. No refund now.' : 'Someone else can have the slot.',
    confirmText: paid && refund === 0 ? 'Release, no refund' : 'Release',
  };
}

/**
 * "You can have 2 bookings coming up at a time, counting match bookings you
 * pay for. Cancel up to 1 hour before." (B8/BE9: a match booking counts for
 * its payer, so the line never says otherwise.)
 */
export function allowanceLine(viewer, cutoffMinutes = null) {
  if (!viewer?.canBook) return viewer?.reason ?? '';
  const parts = [];
  if (viewer.maxActiveBookings != null) {
    parts.push(`You can have ${viewer.maxActiveBookings} booking${viewer.maxActiveBookings === 1 ? '' : 's'} coming up at a time, counting match bookings you pay for.`);
  }
  const cut = cutoffLine(cutoffMinutes);
  if (cut) parts.push(cut + '.');
  return parts.join(' ');
}

/**
 * Grant 2026-11-02 ("Admin can do what they want with a warning"): the one line over Book when the quote carries an
 * admin's chips ("beyond the window", "more than allowed at once"): what the booking breaks, in the admin calendar's own
 * words (overrideLines), and that Book goes ahead regardless, the app's overridesNote word for word. The sheet books in
 * the admin's own name, so "not allowed to book" (about a member they book FOR, X81) is left out. Null with no chips.
 */
export function adminOverridesNote(chips) {
  const lines = overrideLines((chips ?? []).filter((c) => String(c).toLowerCase() !== 'not allowed to book'));
  if (!lines.length) return null;
  return `${lines.join(' ')} Book goes ahead anyway, as an admin.`;
}

// ── Outcomes (BE3) ──
/** What the API's outcome means for the sheet: done, pay, act (3-D Secure), wait or failed. */
export function outcomeStep(res) {
  switch (res?.outcome) {
    case 'confirmed':
    case 'held':
    case 'queued': return 'done';
    case 'needsCard': return 'pay';
    case 'requiresAction': return 'act';
    case 'pendingPayment': return 'wait';
    case 'failed': return 'failed';
    default: return res?.status === 'Confirmed' ? 'done' : 'wait';
  }
}

/** A six-digit code field: digits only, max 6. */
export function cleanCode(text) {
  return String(text ?? '').replace(/\D/g, '').slice(0, 6);
}

export function looksLikeEmail(text) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(text ?? '').trim());
}
